import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { findIndex, uniqBy } from 'lodash';
import { IMessage } from 'src/interfaces';

interface ConversationListState {
  requesting: boolean;
  error: any;
  data: any[];
  total: number;
  success: boolean;
}

interface ConversationState {
  list: ConversationListState;
  mapping: { [key: string]: any };
  activeConversation: any;
}

interface MessageState {
  conversationMap: { [key: string]: { fetching: boolean; items: IMessage[] } };
  sendMessage: any;
  deleteMessage: any;
  receiveMessage: any;
}

interface CombinedState {
  conversations: ConversationState;
  messages: MessageState;
}

const initialConversationState: ConversationState = {
  list: {
    requesting: false,
    error: null,
    data: [],
    total: 0,
    success: false
  },
  mapping: {},
  activeConversation: {}
};

const initialMessageState: MessageState = {
  conversationMap: {},
  sendMessage: {},
  deleteMessage: {},
  receiveMessage: {}
};

const initialState: CombinedState = {
  conversations: initialConversationState,
  messages: initialMessageState
};

const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    // Conversation actions
    resetMessageState(state) {
      state.conversations.list = {
        requesting: false,
        error: null,
        data: [],
        total: 0,
        success: false
      };
      state.conversations.mapping = {};
      state.conversations.activeConversation = {};
    },
    getConversations(state) {
      state.conversations.list.requesting = true;
      state.conversations.list.error = null;
    },
    getConversationsSuccess(state, action: PayloadAction<{ data: any[]; total: number; isMore?: boolean }>) {
      state.conversations.list.requesting = false;

      const newConversationIds = action.payload.data.map(c => c._id);

      if (action.payload.isMore) {
        // Append new conversations to existing ones
        state.conversations.list.data = [...state.conversations.list.data, ...newConversationIds];
      } else {
        // Replace existing conversations
        state.conversations.list.data = newConversationIds;
      }

      state.conversations.list.total = action.payload.total;
      state.conversations.list.success = true;

      // Update mapping
      action.payload.data.forEach((c) => {
        state.conversations.mapping[c._id] = c;
      });
    },
    getConversationsFail(state, action: PayloadAction<any>) {
      state.conversations.list.requesting = false;
      state.conversations.list.error = action.payload;
      state.conversations.list.success = false;
    },
    setActiveConversation(state, action: PayloadAction<any>) {
      // This action triggers the saga
    },
    setActiveConversationSuccess(state, action: PayloadAction<any>) {
      state.conversations.activeConversation = action.payload;
    },
    getConversationDetailSuccess(state, action: PayloadAction<any>) {
      state.conversations.mapping[action.payload._id] = action.payload;
    },
    deactiveConversation(state) {
      state.conversations.activeConversation = {};
    },

    // Message actions
    fetchingMessage(state, action: PayloadAction<string>) {
      const conversationId = action.payload;
      if (!state.messages.conversationMap[conversationId]) {
        state.messages.conversationMap[conversationId] = { fetching: true, items: [] };
      } else {
        state.messages.conversationMap[conversationId].fetching = true;
      }
    },
    loadMessagesSuccess(state, action: PayloadAction<{ conversationId: string; items: IMessage[] }>) {
      const { conversationId, items } = action.payload;
      state.messages.conversationMap[conversationId] = {
        fetching: false,
        items: (items || []).reverse() // Reverse to show oldest first, newest last
      };
    },
    loadMoreMessages(state, action: PayloadAction<any>) {
      // This action triggers the saga to load more messages
    },
    loadMoreMessagesSuccess(state, action: PayloadAction<{ conversationId: string; items: IMessage[] }>) {
      const { conversationId, items } = action.payload;
      if (!state.messages.conversationMap[conversationId]) {
        state.messages.conversationMap[conversationId] = { fetching: false, items: [] };
      }

      const existingItems = state.messages.conversationMap[conversationId].items;
      // Reverse new items and prepend to existing (older messages come first)
      const reversedItems = (items || []).reverse();
      state.messages.conversationMap[conversationId] = {
        fetching: false,
        items: uniqBy([...reversedItems, ...existingItems], '_id')
      };
    },
    sendMessage(state, action: PayloadAction<any>) {
      state.messages.sendMessage = { requesting: true };
    },
    sendMessageSuccess(state, action: PayloadAction<{ conversationId: string; message: IMessage }>) {
      const { conversationId, message } = action.payload;
      state.messages.sendMessage = { requesting: false, success: true };

      // Initialize conversation map if it doesn't exist
      if (!state.messages.conversationMap[conversationId]) {
        state.messages.conversationMap[conversationId] = { fetching: false, items: [] };
      }

      const existingIndex = findIndex(state.messages.conversationMap[conversationId].items, { _id: message._id });
      if (existingIndex > -1) {
        state.messages.conversationMap[conversationId].items[existingIndex] = message;
      } else {
        state.messages.conversationMap[conversationId].items.push(message);
      }
    },
    sendMessageFail(state, action: PayloadAction<any>) {
      state.messages.sendMessage = { requesting: false, error: action.payload };
    },
    readMessages(state, action: PayloadAction<string>) {
      const conversationId = action.payload;
      if (state.messages.conversationMap[conversationId]) {
        state.messages.conversationMap[conversationId].items.forEach((message: any) => {
          message.isRead = true;
        });
      }
    },
    deletePrivateMessage(state, action: PayloadAction<any>) {
      state.messages.deleteMessage = { requesting: true };
    },
    deletePrivateMessageSuccess(state, action: PayloadAction<{ messageId: string }>) {
      const { messageId } = action.payload;
      state.messages.deleteMessage = { requesting: false, success: true };

      // Remove from all conversation maps
      Object.keys(state.messages.conversationMap).forEach((conversationId) => {
        state.messages.conversationMap[conversationId].items =
          state.messages.conversationMap[conversationId].items.filter(m => m._id !== messageId);
      });
    },
    deletePrivateMessageFail(state, action: PayloadAction<any>) {
      state.messages.deleteMessage = { requesting: false, error: action.payload };
    },
    deleteMessageSuccess(state, action: PayloadAction<{ messageId: string }>) {
      const { messageId } = action.payload;

      // Remove from all conversation maps
      Object.keys(state.messages.conversationMap).forEach((conversationId) => {
        state.messages.conversationMap[conversationId].items =
          state.messages.conversationMap[conversationId].items.filter(m => m._id !== messageId);
      });
    },
    // Add missing action creators for saga compatibility
    loadMessages(state, action: PayloadAction<any>) {
      // This action triggers the saga to load messages
    },
    getConversationDetail(state, action: PayloadAction<any>) {
      // This action triggers the saga to get conversation details
    }
  }
});

export const {
  resetMessageState,
  getConversations,
  getConversationsSuccess,
  getConversationsFail,
  setActiveConversation,
  setActiveConversationSuccess,
  getConversationDetailSuccess,
  deactiveConversation,
  fetchingMessage,
  loadMessages,
  loadMessagesSuccess,
  loadMoreMessages,
  loadMoreMessagesSuccess,
  sendMessage,
  sendMessageSuccess,
  sendMessageFail,
  readMessages,
  deletePrivateMessage,
  deletePrivateMessageSuccess,
  deletePrivateMessageFail,
  deleteMessageSuccess,
  getConversationDetail
} = messageSlice.actions;

export default messageSlice.reducer;